import type {
    CategoryInput,
    FieldErrors,
    TransactionInput,
    TransactionType
} from '@/types'

import { addDays, isValidDateString } from '@/lib/dates'

import {
    LIMITS,
    isCategoryIcon,
    isPaymentMethod,
    isTransactionType
} from '@/constants/finance'

type Result<T, K extends string> = { ok: true, data: T } | { ok: false, errors: FieldErrors<K> }

const asText = (value: unknown): string => (typeof value === 'string' ? value.trim() : '')

const asAmount = (value: unknown): number => {
    if (typeof value === 'number') return value
    if (typeof value === 'string' && value.trim() !== '') return Number(value.replace(/\./g, ''))
    return Number.NaN
}

export type TransactionField = keyof TransactionInput

/**
 * Shared by the form and the API. `latestDate` is the last allowed date (today, plus a
 * one day tolerance on the server so other Indonesian time zones never get refused).
 */
export const validateTransaction = (input: Record<string, unknown>, latestDate: string): Result<TransactionInput, TransactionField> => {
    const errors: FieldErrors<TransactionField> = {}

    const type = input.type
    const amount = asAmount(input.amount)
    const title = asText(input.title)
    const categoryId = asText(input.categoryId)
    const date = asText(input.date)
    const paymentMethod = input.paymentMethod
    const notes = asText(input.notes)

    if (!isTransactionType(type)) errors.type = 'Pilih jenis transaksi.'

    if (!Number.isFinite(amount) || amount === 0) errors.amount = 'Masukkan nominal transaksi.'
    else if (!Number.isInteger(amount) || amount < 0) errors.amount = 'Nominal harus berupa angka bulat lebih dari Rp 0.'
    else if (amount > LIMITS.amountMax) errors.amount = 'Nominal terlalu besar. Maksimal Rp 999.999.999.999.'

    if (!title) errors.title = 'Isi judul atau deskripsi transaksi.'
    else if (title.length > LIMITS.titleMax) errors.title = `Judul maksimal ${LIMITS.titleMax} karakter.`

    if (!categoryId) errors.categoryId = 'Pilih kategori transaksi.'

    if (!date) errors.date = 'Pilih tanggal transaksi.'
    else if (!isValidDateString(date)) errors.date = 'Format tanggal tidak valid.'
    else if (date > latestDate) errors.date = 'Tanggal tidak boleh melewati hari ini.'

    if (!isPaymentMethod(paymentMethod)) errors.paymentMethod = 'Pilih metode pembayaran.'

    if (notes.length > LIMITS.notesMax) errors.notes = `Catatan maksimal ${LIMITS.notesMax} karakter.`

    if (Object.keys(errors).length > 0) return { ok: false, errors }

    return {
        ok: true,
        data: {
            type: type as TransactionType,
            amount,
            title,
            categoryId,
            date,
            paymentMethod: paymentMethod as TransactionInput['paymentMethod'],
            notes
        }
    }
}

export const serverLatestDate = (today: string): string => addDays(today, 1)

export type CategoryField = keyof CategoryInput

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/

export const validateCategory = (input: Record<string, unknown>): Result<CategoryInput, CategoryField> => {
    const errors: FieldErrors<CategoryField> = {}

    const name = asText(input.name).replace(/\s+/g, ' ')
    const type = input.type
    const color = asText(input.color)
    const icon = input.icon

    if (!name) errors.name = 'Isi nama kategori.'
    else if (name.length > LIMITS.categoryNameMax) errors.name = `Nama kategori maksimal ${LIMITS.categoryNameMax} karakter.`

    if (!isTransactionType(type)) errors.type = 'Pilih jenis kategori.'
    if (!HEX_COLOR.test(color)) errors.color = 'Pilih warna kategori.'
    if (!isCategoryIcon(icon)) errors.icon = 'Pilih ikon kategori.'

    if (Object.keys(errors).length > 0) return { ok: false, errors }

    return {
        ok: true,
        data: {
            name,
            type: type as TransactionType,
            color: color.toUpperCase(),
            icon: icon as string
        }
    }
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

export const validateName = (value: unknown): string | null => {
    const name = asText(value).replace(/\s+/g, ' ')
    if (!name) return 'Isi nama lengkapmu.'
    if (name.length < LIMITS.nameMin) return `Nama minimal ${LIMITS.nameMin} karakter.`
    if (name.length > LIMITS.nameMax) return `Nama maksimal ${LIMITS.nameMax} karakter.`
    return null
}

export const validateEmail = (value: unknown): string | null => {
    const email = asText(value)
    if (!email) return 'Isi alamat email.'
    if (email.length > LIMITS.emailMax || !EMAIL_PATTERN.test(email)) return 'Format email belum benar, contoh: nama@email.com.'
    return null
}

export const validateNewPassword = (value: unknown): string | null => {
    const password = typeof value === 'string' ? value : ''
    if (!password) return 'Buat kata sandi.'
    if (password.length < LIMITS.passwordMin) return `Kata sandi minimal ${LIMITS.passwordMin} karakter.`
    if (password.length > LIMITS.passwordMax) return `Kata sandi maksimal ${LIMITS.passwordMax} karakter.`
    if (!/[A-Za-z]/.test(password) || !/\d/.test(password)) return 'Gunakan kombinasi huruf dan angka.'
    return null
}

export const normalizeName = (value: unknown): string => asText(value).replace(/\s+/g, ' ')

export const normalizeEmail = (value: unknown): string => asText(value).toLowerCase()
